import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { externalReviewAPI } from '../../api/services';

const ExternalReviewSubmission: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [requestedByName, setRequestedByName] = useState('');
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    rating: 0,
    feedback: '',
    strengths: '',
    improvements: '',
    reviewerName: '',
  });

  const [hoverRating, setHoverRating] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchReviewDetails();
  }, [token]);

  const fetchReviewDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await externalReviewAPI.getReviewByToken(token!);
      const data = response.data.data;

      setProjectName(data.projectName);
      setProjectDescription(data.projectDescription);
      setRequestedByName(data.requestedByName);
      setReviewerEmail(data.reviewerEmail);
      setExpiresAt(data.expiresAt);

      if (data.reviewerName) {
        setFormData((prev) => ({ ...prev, reviewerName: data.reviewerName }));
      }
    } catch (err: any) {
      console.error('Error fetching review details:', err);
      if (err.response?.status === 410) {
        setError('This review link has expired.');
      } else if (err.response?.status === 400) {
        setError('This review has already been submitted.');
      } else {
        setError(err.response?.data?.message || 'Failed to load review details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (formData.rating === 0) {
      newErrors.rating = 'Please select a star rating before submitting.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      setSubmitting(true);
      setError('');

      await externalReviewAPI.submitReview(token!, formData);
      setSuccess(true);
    } catch (err: any) {
      console.error('Error submitting review:', err);
      const msg: string = err.response?.data?.message || '';
      if (msg.includes('already been submitted') || err.response?.status === 400) {
        setError('This review has already been submitted. Thank you for your earlier response!');
      } else if (err.response?.status === 410) {
        setError('This review link has expired. Please contact the person who sent it.');
      } else {
        setError(msg || 'Something went wrong. Please try again in a moment.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => { setFormData({ ...formData, rating: star }); setErrors(prev => ({ ...prev, rating: '' })); }}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <svg
              className={`w-10 h-10 ${
                star <= (hoverRating || formData.rating)
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300'
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <svg
              className="w-20 h-20 text-green-500 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h1>
          <p className="text-gray-600 mb-6">
            Your review has been submitted successfully. We appreciate your valuable feedback!
          </p>
          <p className="text-sm text-gray-500">
            Your feedback will help improve the project and provide valuable insights to the team.
          </p>
        </div>
      </div>
    );
  }

  if (error && !projectName) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <svg
              className="w-20 h-20 text-red-500 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Oops!</h1>
          <p className="text-gray-600 mb-2">{error}</p>
          <p className="text-sm text-gray-500">
            Please contact the person who sent you this link if you believe this is an error.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-xl p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Project Review Request</h1>
          <p className="text-gray-600 mb-6">
            You've been invited by <span className="font-semibold">{requestedByName}</span> to review
            this project
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">{projectName}</h2>
            <p className="text-gray-700">{projectDescription}</p>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>
              Expires: {new Date(expiresAt).toLocaleDateString()} at{' '}
              {new Date(expiresAt).toLocaleTimeString()}
            </span>
          </div>
        </div>

        {/* Review Form */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Share Your Feedback</h2>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Reviewer Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Name (Optional)
              </label>
              <input
                type="text"
                value={formData.reviewerName}
                onChange={(e) => setFormData({ ...formData, reviewerName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your name"
              />
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Overall Rating *
              </label>
              {renderStars()}
              {formData.rating > 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  {formData.rating === 1 && 'Poor'}
                  {formData.rating === 2 && 'Fair'}
                  {formData.rating === 3 && 'Good'}
                  {formData.rating === 4 && 'Very Good'}
                  {formData.rating === 5 && 'Excellent'}
                </p>
              )}
              {errors.rating && <p className="mt-2 text-xs text-red-600">{errors.rating}</p>}
            </div>

            {/* Strengths */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What are the project's strengths?
              </label>
              <textarea
                rows={4}
                value={formData.strengths}
                onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="What did you like about this project?"
                maxLength={2000}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.strengths.length}/2000</p>
            </div>

            {/* Improvements */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What could be improved?
              </label>
              <textarea
                rows={4}
                value={formData.improvements}
                onChange={(e) => setFormData({ ...formData, improvements: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Suggestions for improvement"
                maxLength={2000}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.improvements.length}/2000</p>
            </div>

            {/* General Feedback */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Feedback
              </label>
              <textarea
                rows={6}
                value={formData.feedback}
                onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any additional comments or feedback..."
                maxLength={5000}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.feedback.length}/5000</p>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting || formData.rating === 0}
                className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                * Required fields
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-600 text-sm">
          <p>This review is confidential and will be shared with the project team.</p>
        </div>
      </div>
    </div>
  );
};

export default ExternalReviewSubmission;
